import { ChevronLeft, ChevronRight, Loader2, ServerOff } from 'lucide-react';
import { SiteCard } from './SiteCard';
import { useDashboardStore } from '../store/dashboardStore';
import type { Site } from '../hooks/useSites';
import type { FleetLiveEntry } from '../hooks/useFleetTelemetry';

interface SiteGridProps {
  sites: Site[];
  isLoading: boolean;
  liveBySite?: Map<string, FleetLiveEntry>;
  nowMs?: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
  onSiteClick?: (siteId: string) => void;
}

export function SiteGrid({ sites, isLoading, liveBySite, nowMs, pagination, onSiteClick }: SiteGridProps) {
  const { currentPage, setPage } = useDashboardStore();

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-ems-green animate-spin mb-3" />
        <p className="text-ems-text-muted text-sm">Loading sites...</p>
      </div>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 rounded-2xl bg-ems-bg-card border border-ems-border">
        <ServerOff className="w-12 h-12 text-ems-text-muted mb-3" />
        <h3 className="text-base font-semibold text-ems-text-primary mb-1">No Sites Found</h3>
        <p className="text-ems-text-muted text-sm">Try adjusting your filters or search criteria.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
        {sites.map((site) => (
          <SiteCard
            key={site.site_id}
            site={site}
            live={liveBySite?.get(site.site_id)}
            nowMs={nowMs}
            onClick={onSiteClick}
          />
        ))}
      </div>

      {pagination && pagination.total_pages > 1 && (
        <div className="flex items-center justify-between p-3 bg-ems-bg-card rounded-2xl border border-ems-border">
          <div className="text-sm text-ems-text-muted font-mono">
            {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1)
              .filter((page) => page === 1 || page === pagination.total_pages || Math.abs(page - currentPage) <= 1)
              .map((page, index, array) => {
                const prevPage = array[index - 1];
                const showEllipsis = prevPage && page - prevPage > 1;
                return (
                  <span key={page} className="flex items-center">
                    {showEllipsis && <span className="px-1.5 text-ems-text-muted">...</span>}
                    <button
                      onClick={() => setPage(page)}
                      className={`px-2.5 py-1 rounded-lg text-sm font-medium transition-colors ${
                        page === currentPage
                          ? 'bg-ems-forest text-ems-text-primary'
                          : 'text-ems-text-secondary hover:bg-ems-bg-card-hover'
                      }`}
                    >
                      {page}
                    </button>
                  </span>
                );
              })}
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage === pagination.total_pages}
              className="p-1.5 rounded-lg border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
